import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { corpusService, CorpusWordWithSectors } from '../../services/corpusService';
import { Card, CardContent, CardHeader } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/Tabs';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faFileImport, faFileExport, faSearch } from '@fortawesome/free-solid-svg-icons';
import WordsTable from '../../components/corpus/WordsTable';
import AddWordModal from '../../components/corpus/AddWordModal';
import EditWordModal from '../../components/corpus/EditWordModal';
import ImportWordsModal from '../../components/corpus/ImportWordsModal';
import toast from 'react-hot-toast';

export default function CorpusPage() {
  const queryClient = useQueryClient();
  const [selectedSectorId, setSelectedSectorId] = useState<number | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedLevel, setSelectedLevel] = useState<string | null>(null);
  const [currentPageBySector, setCurrentPageBySector] = useState<Record<number, number>>({});
  const [wordsBySector, setWordsBySector] = useState<Record<number, CorpusWordWithSectors[]>>({});
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [selectedWord, setSelectedWord] = useState<CorpusWordWithSectors | null>(null);

  // Get current page for selected sector
  const currentPage = selectedSectorId ? (currentPageBySector[selectedSectorId] || 1) : 1;
  const allWords = selectedSectorId ? (wordsBySector[selectedSectorId] || []) : [];

  // Fetch sectors
  const { data: sectorsData, isLoading: sectorsLoading } = useQuery({
    queryKey: ['sectors'],
    queryFn: () => corpusService.getSectors(),
  });

  // Fetch words for selected sector with pagination
  const { data: wordsData, isLoading: wordsLoading, isFetching: wordsFetching } = useQuery({
    queryKey: ['corpus-words', selectedSectorId, searchQuery, selectedLevel, currentPage],
    queryFn: () => {
      if (!selectedSectorId) return null;
      return corpusService.getSectorWords(selectedSectorId, {
        search: searchQuery || undefined,
        level: selectedLevel || undefined,
        page: currentPage,
        limit: 100,
      });
    },
    enabled: !!selectedSectorId,
    placeholderData: (previousData) => previousData,
  });

  // Update words for the selected sector when new data arrives
  useEffect(() => {
    if (wordsData?.words && selectedSectorId) {
      setWordsBySector(prev => {
        const currentPageForSector = currentPageBySector[selectedSectorId] || 1;
        if (currentPageForSector === 1) {
          // First page: replace words for this sector
          return { ...prev, [selectedSectorId]: wordsData.words };
        } else {
          // Subsequent pages: append words for this sector
          const existingWords = prev[selectedSectorId] || [];
          return { ...prev, [selectedSectorId]: [...existingWords, ...wordsData.words] };
        }
      });
    }
  }, [wordsData, selectedSectorId, currentPageBySector]);

  // Reset to page 1 when sector, search, or level changes
  useEffect(() => {
    if (selectedSectorId) {
      setCurrentPageBySector(prev => ({ ...prev, [selectedSectorId]: 1 }));
      // Clear this sector's words when searching or filtering
      if (searchQuery || selectedLevel) {
        setWordsBySector(prev => ({ ...prev, [selectedSectorId]: [] }));
      }
    }
  }, [selectedSectorId, searchQuery, selectedLevel]);

  // Fetch statistics
  const { data: statsData } = useQuery({
    queryKey: ['corpus-stats'],
    queryFn: () => corpusService.getCorpusStats(),
  });

  // Set initial sector when data loads
  useEffect(() => {
    if (sectorsData?.sectors && sectorsData.sectors.length > 0 && !selectedSectorId) {
      // Find General sector or use first sector
      const generalSector = sectorsData.sectors.find(s => s.code === 'GENERAL');
      setSelectedSectorId(generalSector?.id || sectorsData.sectors[0].id);
    }
  }, [sectorsData, selectedSectorId]);

  // Delete word mutation
  const deleteMutation = useMutation({
    mutationFn: (wordId: number) => corpusService.deleteWord(wordId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['corpus-words'] });
      queryClient.invalidateQueries({ queryKey: ['corpus-stats'] });
      toast.success('Palabra eliminada exitosamente');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Error al eliminar palabra');
    },
  });

  // Export words
  const handleExport = async (format: 'json' | 'txt') => {
    try {
      const blob = await corpusService.exportWords({
        sectorId: selectedSectorId || undefined,
        format,
      });
      const filename = `corpus_${selectedSectorId || 'todos'}_${Date.now()}.${format}`;
      corpusService.downloadExportedFile(blob, filename);
      toast.success('Corpus exportado exitosamente');
    } catch (error: any) {
      toast.error('Error al exportar corpus');
    }
  };

  const handleEdit = (word: CorpusWordWithSectors) => {
    setSelectedWord(word);
    setIsEditModalOpen(true);
  };

  const handleDelete = (wordId: number) => {
    if (window.confirm('¿Estás seguro de que quieres eliminar esta palabra?')) {
      deleteMutation.mutate(wordId);
    }
  };

  const handleLoadMore = () => {
    if (selectedSectorId) {
      setCurrentPageBySector(prev => ({
        ...prev,
        [selectedSectorId]: (prev[selectedSectorId] || 1) + 1
      }));
    }
  };

  if (sectorsLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-500">Cargando sectores...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-green-600 to-teal-600 rounded-xl p-8 shadow-lg">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-3xl font-bold mb-2 text-white">Corpus Léxico</h1>
            <p className="text-white/90">
              {statsData?.stats.total || 0} palabras en el corpus
            </p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button
              onClick={() => setIsImportModalOpen(true)}
              className="bg-white/20 text-white border-white/30 hover:bg-white/30"
            >
              <FontAwesomeIcon icon={faFileImport} className="mr-2" />
              Importar
            </Button>
            <Button
              onClick={() => handleExport('json')}
              className="bg-white/20 text-white border-white/30 hover:bg-white/30"
            >
              <FontAwesomeIcon icon={faFileExport} className="mr-2" />
              Exportar JSON
            </Button>
            <Button
              onClick={() => handleExport('txt')}
              className="bg-white/20 text-white border-white/30 hover:bg-white/30"
            >
              <FontAwesomeIcon icon={faFileExport} className="mr-2" />
              Exportar TXT
            </Button>
          </div>
        </div>
      </div>

      {/* Statistics Cards */}
      {statsData && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardContent className="p-6">
              <div className="text-center">
                <p className="text-gray-500 text-sm mb-1">Total de Palabras</p>
                <p className="text-3xl font-bold text-green-600">{statsData.stats.total}</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <div className="text-center">
                <p className="text-gray-500 text-sm mb-1">Nivel C1+</p>
                <p className="text-3xl font-bold text-orange-600">
                  {(statsData.stats.byLevel.find((l) => l.mcerlLevel === 'C1')?. count || 0) +
                    (statsData.stats.byLevel.find((l) => l.mcerlLevel === 'C2')?.count || 0)}
                </p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <div className="text-center">
                <p className="text-gray-500 text-sm mb-1">Sectores Activos</p>
                <p className="text-3xl font-bold text-blue-600">
                  {statsData.stats.bySector.length}
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Main Content */}
      <Card>
        <CardHeader className="border-b">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <h2 className="text-xl font-semibold text-gray-800">Palabras por Sector</h2>
            <div className="flex gap-2 items-center flex-wrap">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Buscar palabras..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                />
                <FontAwesomeIcon
                  icon={faSearch}
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                />
              </div>
              <Button onClick={() => setIsAddModalOpen(true)} className="bg-green-600 hover:bg-green-700">
                <FontAwesomeIcon icon={faPlus} className="mr-2" />
                Añadir Palabra
              </Button>
            </div>
          </div>

          {/* MCERL Level Filter */}
          <div className="mt-4 pt-4 border-t">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm font-medium text-gray-700">Filtrar por Nivel MCERL:</span>
              <div className="flex gap-2 flex-wrap">
                <Button
                  size="sm"
                  variant={selectedLevel === null ? 'default' : 'outline'}
                  onClick={() => setSelectedLevel(null)}
                  className={selectedLevel === null ? 'bg-green-600 hover:bg-green-700' : ''}
                >
                  Todos
                </Button>
                {['A1', 'A2', 'B1', 'B2', 'C1', 'C2'].map((level) => (
                  <Button
                    key={level}
                    size="sm"
                    variant={selectedLevel === level ? 'default' : 'outline'}
                    onClick={() => setSelectedLevel(level)}
                    className={
                      selectedLevel === level
                        ? level.startsWith('C')
                          ? 'bg-orange-600 hover:bg-orange-700'
                          : level.startsWith('B')
                          ? 'bg-blue-600 hover:bg-blue-700'
                          : 'bg-green-600 hover:bg-green-700'
                        : level.startsWith('C')
                        ? 'border-orange-300 text-orange-700 hover:bg-orange-50'
                        : level.startsWith('B')
                        ? 'border-blue-300 text-blue-700 hover:bg-blue-50'
                        : 'border-green-300 text-green-700 hover:bg-green-50'
                    }
                  >
                    {level}
                  </Button>
                ))}
              </div>
              {selectedLevel && (
                <span className="text-sm text-gray-500 ml-2">
                  Mostrando solo palabras de nivel {selectedLevel}
                </span>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-6">
          {sectorsData?.sectors && sectorsData.sectors.length > 0 ? (
            <Tabs
              value={selectedSectorId?.toString() || sectorsData.sectors[0].id.toString()}
              onValueChange={(value) => setSelectedSectorId(Number(value))}
            >
              <TabsList className="w-full mb-6 flex-wrap h-auto">
                {sectorsData.sectors
                  .slice()
                  .sort((a, b) => {
                    if (a.code === 'GENERAL') return -1;
                    if (b.code === 'GENERAL') return 1;
                    return a.name.localeCompare(b.name);
                  })
                  .map((sector) => (
                    <TabsTrigger
                      key={sector.id}
                      value={sector.id.toString()}
                      className="flex items-center gap-2"
                    >
                      {sector.icon && <span>{sector.icon}</span>}
                      <span>{sector.name}</span>
                      <span className="ml-1 text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">
                        {statsData?.stats.bySector.find((s) => s.sector.id === sector.id)?.count || 0}
                      </span>
                    </TabsTrigger>
                  ))}
              </TabsList>

              {sectorsData.sectors.map((sector) => (
                <TabsContent key={sector.id} value={sector.id.toString()}>
                  {wordsLoading && currentPage === 1 && allWords.length === 0 ? (
                    <div className="flex items-center justify-center py-12">
                      <div className="text-center">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto mb-4"></div>
                        <p className="text-gray-500">Cargando palabras...</p>
                      </div>
                    </div>
                  ) : allWords.length === 0 ? (
                    <div className="text-center py-12">
                      <p className="text-gray-500">No se encontraron palabras</p>
                    </div>
                  ) : (
                    <>
                      <WordsTable
                        words={allWords}
                        onEdit={handleEdit}
                        onDelete={handleDelete}
                      />

                      {/* Load More Button */}
                      {wordsData && wordsData.hasMore && allWords.length > 0 && (
                        <div className="flex justify-center mt-6">
                          <Button
                            onClick={handleLoadMore}
                            disabled={wordsFetching}
                            className="bg-green-600 hover:bg-green-700 px-8"
                          >
                            {wordsFetching ? (
                              <>
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                Cargando...
                              </>
                            ) : (
                              <>
                                Cargar más ({wordsData.total - allWords.length} restantes)
                              </>
                            )}
                          </Button>
                        </div>
                      )}

                      {/* Stats */}
                      {allWords.length > 0 && wordsData && (
                        <div className="mt-4 text-center text-sm text-gray-500">
                          Mostrando {allWords.length} de {wordsData.total} palabras
                        </div>
                      )}
                    </>
                  )}
                </TabsContent>
              ))}
            </Tabs>
          ) : (
            <div className="text-center py-12">
              <p className="text-gray-500">No hay sectores disponibles</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modals */}
      <AddWordModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        sectors={sectorsData?.sectors || []}
        defaultSectorId={selectedSectorId || undefined}
      />

      {selectedWord && (
        <EditWordModal
          isOpen={isEditModalOpen}
          onClose={() => {
            setIsEditModalOpen(false);
            setSelectedWord(null);
          }}
          word={selectedWord}
          sectors={sectorsData?.sectors || []}
        />
      )}

      <ImportWordsModal
        isOpen={isImportModalOpen}
        onClose={() => setIsImportModalOpen(false)}
        sectors={sectorsData?.sectors || []}
        defaultSectorId={selectedSectorId || undefined}
      />
    </div>
  );
}
